import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  OmschrijvingEnBedrag,
  Onderneming
} from "@snelstart-ventures/fiscaal/domain-logic";
import {filter, Observable, shareReplay, take, tap} from "rxjs";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {debounceTime} from "rxjs/operators";

type OmschrijvingBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-winstcorrecties',
  templateUrl: './winstcorrecties.component.html',
  styleUrls: ['./winstcorrecties.component.scss'],
  imports: [
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class WinstcorrectiesComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public indexOnderneming = 0;
  public isOndernemer = false;
  public isPartner = false;

  public formGroup = new FormGroup({
    nietAftrekbareKosten: new FormArray<OmschrijvingBedragFormGroup>([]),
    beperktAftrekbareKosten: new FormArray<OmschrijvingBedragFormGroup>([]),
    vrijgesteldeWinst: new FormArray<OmschrijvingBedragFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.beperktAftrekbareKosten.valueChanges
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.saveForm())
      ).subscribe();
    this.nietAftrekbareKosten.valueChanges
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.saveForm())
      ).subscribe();
    this.vrijgesteldeWinst.valueChanges
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.saveForm())
      ).subscribe();
  }

  ngOnDestroy(): void {
    this.dataService.calculateLatestIncomeTaxReturn();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.formGroup.reset({}, {emitEvent: false});
    this.isOndernemer = false;
    if (details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)) {
      this.isOndernemer = true;
      this.updateForm(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.nietAftrekbareKosten || [], this.nietAftrekbareKosten);
      this.updateForm(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.beperktAftrekbareKosten || [], this.beperktAftrekbareKosten);
      this.updateForm(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.wuoVrijgesteldeWinstbestanddelen || [], this.vrijgesteldeWinst);
    }
  }

  get nietAftrekbareKosten(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.nietAftrekbareKosten;
  }

  addRijNietAftrekbareKosten(): void {
    this.nietAftrekbareKosten.push(this.createFormGroup(), { emitEvent: false });
  }

  deleteNietAftrekbareKosten(index: number): void {
    this.nietAftrekbareKosten.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get beperktAftrekbareKosten(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.beperktAftrekbareKosten;
  }

  addRijBeperktAftrekbareKosten(): void {
    this.beperktAftrekbareKosten.push(this.createFormGroup(), { emitEvent: false });
  }

  deleteBeperktAftrekbareKosten(index: number): void {
    this.beperktAftrekbareKosten.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get vrijgesteldeWinst(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.vrijgesteldeWinst;
  }

  addVrijgesteldeWinst(): void {
    this.vrijgesteldeWinst.push(this.createFormGroup(), { emitEvent: false });
  }

  deleteVrijgesteldeWinst(index: number): void {
    this.vrijgesteldeWinst.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(item?: OmschrijvingEnBedrag): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
    })
  }

  private updateForm(loon: OmschrijvingEnBedrag[], form : FormArray): void {
    let lijst = loon || [];
    const formArray = form;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};
        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          wuoVrijgesteldeWinstbestanddelen: formValue.vrijgesteldeWinst,
          beperktAftrekbareKosten: formValue.beperktAftrekbareKosten,
          nietAftrekbareKosten: formValue.nietAftrekbareKosten,
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
          ...currentEntity.aangifte,
          partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
        }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }

}


